import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/store/auth";
import type { UserRole } from "@/types";

function Spinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-cream">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-navy-100 border-t-navy-800" />
    </div>
  );
}

interface ProtectedRouteProps {
  children: ReactNode;
  /** If set, only users with this exact role may view the route (e.g. "admin"). */
  requireRole?: UserRole;
}

/** Guards private pages: unauthenticated -> /login, wrong role -> /dashboard. */
export default function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "loading" || status === "idle") return <Spinner />;

  if (status === "unauthenticated") {
    // Preserve where they were headed so login can send them back.
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnTo=${returnTo}`} replace />;
  }

  if (requireRole && user?.role !== requireRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Guards auth-only pages (login/signup): an already-authenticated user is
 * sent straight to the Home page (never the dashboard). Doing it here also
 * avoids a redirect race with the login page's own navigation.
 */
export function PublicOnlyRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === "loading" || status === "idle") return <Spinner />;
  if (status === "authenticated") return <Navigate to="/home" replace />;

  return <>{children}</>;
}